import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { Info } from 'lucide-react';

/**
 * PaymentBreakdownPopover
 * ----------------------------------------------------------------
 * At-a-glance summary of how an invoice was paid. Renders a tiny info
 * icon that opens a lightweight card on desktop-hover / mobile-tap.
 *
 * All data is derived from the sale object already returned by the
 * Sales list endpoint — NO extra API round-trip.
 *
 * Sale shape supported:
 *   {
 *     amount:         number,      // invoice total
 *     payment_method: string,      // 'cash'|'card'|'bank_transfer'|'cheque'|'due'|'split'
 *     payments:       Array<{method, amount, cheques?}> | null,   // split only
 *     cheques:        Array<{bank_name, cheque_number, deposit_date, amount, status}> | null,
 *     cheque_bank_name / cheque_number / cheque_deposit_date / cheque_status,  // legacy single-cheque
 *     is_due_paid:    bool,
 *   }
 */

const METHOD_META = {
    cash:          { icon: '💵', label: 'Cash' },
    card:          { icon: '💳', label: 'Card' },
    bank_transfer: { icon: '🏦', label: 'Bank Transfer' },
    cheque:        { icon: '🧾', label: 'Cheque' },
    due:           { icon: '⏳', label: 'Due' },
};

const formatEUR = (v) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);

/**
 * Normalise the sale into a stable `{ lines, cheques, totalPaid, totalDue, invoiceTotal }`
 * shape so the render layer stays dumb.
 */
function buildBreakdown(sale) {
    const invoiceTotal = Number(sale.amount) || 0;
    const method = sale.payment_method;

    let lines = [];
    let cheques = [];

    if (method === 'split' && Array.isArray(sale.payments)) {
        // Aggregate identical methods (e.g. two cash rows → one line) —
        // cheques stay per-cheque under the cheques[] block below.
        const totals = {};
        for (const p of sale.payments) {
            const m = p.method;
            if (!m) continue;
            totals[m] = (totals[m] || 0) + (Number(p.amount) || 0);
        }
        lines = Object.entries(totals).map(([m, amt]) => ({ method: m, amount: amt }));
        cheques = Array.isArray(sale.cheques) ? sale.cheques : [];
    } else if (method === 'cheque') {
        // Legacy single-cheque sale — one cheque row.
        lines = [{ method: 'cheque', amount: invoiceTotal }];
        if (sale.cheque_bank_name || sale.cheque_number) {
            cheques = [{
                bank_name: sale.cheque_bank_name,
                cheque_number: sale.cheque_number,
                deposit_date: sale.cheque_deposit_date,
                amount: invoiceTotal,
                status: sale.cheque_status || 'pending',
            }];
        }
    } else if (method) {
        lines = [{ method, amount: invoiceTotal }];
    }

    // Total paid = every non-due line + every cheque line + the due
    // slice IFF it has been settled (is_due_paid=true). Otherwise the
    // outstanding due amount is called out separately in the footer.
    const dueLines = lines.filter((l) => l.method === 'due');
    const paidLines = lines.filter((l) => l.method !== 'due');
    const totalDue = dueLines.reduce((s, l) => s + l.amount, 0);
    const totalPaid =
        paidLines.reduce((s, l) => s + l.amount, 0) +
        (sale.is_due_paid ? totalDue : 0);
    // If the due slice has been settled (is_due_paid), no outstanding.
    const outstanding = sale.is_due_paid ? 0 : totalDue;

    return { lines, cheques, totalPaid, totalDue, outstanding, invoiceTotal };
}

const Row = ({ icon, label, amount, extra, testid }) => (
    <div className="flex items-center justify-between text-sm py-1" data-testid={testid}>
        <span className="flex items-center gap-2 text-[#4A4A4A]">
            <span aria-hidden="true" className="w-5 text-center">{icon}</span>
            <span>{label}</span>
            {extra && <span className="text-[11px] text-gray-400">{extra}</span>}
        </span>
        <span className="font-mono font-semibold text-[#1A1A1A]">{formatEUR(amount)}</span>
    </div>
);

const StatusPill = ({ status }) => {
    const map = {
        pending:   'bg-yellow-100 text-yellow-800',
        deposited: 'bg-blue-100 text-blue-800',
        cleared:   'bg-green-100 text-green-800',
        bounced:   'bg-red-100 text-red-800',
    };
    return (
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${map[status] || 'bg-gray-100 text-gray-600'}`}>
            {status}
        </span>
    );
};

export const PaymentBreakdownPopover = ({ sale, testIdSuffix }) => {
    const breakdown = buildBreakdown(sale);
    if (!breakdown.lines.length && !breakdown.cheques.length) return null;

    // Aggregated method rows — every non-cheque line (cheques get their own
    // per-cheque section below, so we skip them here when we have cheque
    // details to render).
    const chequeLines = breakdown.lines.filter((l) => l.method === 'cheque');
    const nonChequeLines = breakdown.lines.filter((l) => l.method !== 'cheque' && l.method !== 'due');
    const dueLines = breakdown.lines.filter((l) => l.method === 'due');

    const hasDetailedCheques = breakdown.cheques.length > 0;

    return (
        <HoverCard openDelay={80} closeDelay={80}>
            <HoverCardTrigger asChild>
                <button
                    type="button"
                    aria-label="View payment breakdown"
                    data-testid={`payment-breakdown-trigger-${testIdSuffix || sale.id}`}
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full text-gray-400 hover:text-[#C08478] hover:bg-[#FBF8F6] transition-colors focus:outline-none focus:ring-2 focus:ring-[#C08478]/40"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Info size={14} />
                </button>
            </HoverCardTrigger>
            <HoverCardContent
                className="w-80 p-0 shadow-lg border border-[#EFE5E1]"
                sideOffset={6}
                data-testid={`payment-breakdown-content-${testIdSuffix || sale.id}`}
            >
                <div className="px-4 py-3 border-b border-[#EFE5E1] bg-[#FBF8F6] rounded-t-md">
                    <p className="text-[11px] uppercase tracking-[1.5px] text-[#C08478] font-semibold">
                        Payment Breakdown
                    </p>
                </div>

                <div className="px-4 py-3 space-y-0.5">
                    {/* Non-cheque, non-due methods */}
                    {nonChequeLines.map((l) => (
                        <Row
                            key={l.method}
                            icon={METHOD_META[l.method]?.icon || '•'}
                            label={METHOD_META[l.method]?.label || l.method}
                            amount={l.amount}
                            testid={`pb-line-${l.method}`}
                        />
                    ))}

                    {/* Cheque section — per-cheque detail if available, else aggregate */}
                    {hasDetailedCheques ? (
                        <div className="pt-1" data-testid="pb-cheques-block">
                            <div className="flex items-center gap-2 text-sm text-[#4A4A4A] py-1">
                                <span aria-hidden="true" className="w-5 text-center">🧾</span>
                                <span className="font-medium">
                                    {breakdown.cheques.length > 1 ? 'Cheques' : 'Cheque'}
                                </span>
                            </div>
                            {breakdown.cheques.map((c, i) => (
                                <div
                                    key={c.id || i}
                                    className="ml-7 flex items-center justify-between text-xs py-0.5"
                                    data-testid={`pb-cheque-${i}`}
                                >
                                    <div className="flex flex-col min-w-0 pr-2">
                                        <span className="font-mono text-[#1A1A1A] truncate">
                                            #{c.cheque_number || c.number || '—'}
                                        </span>
                                        <span className="text-gray-400 truncate">
                                            {c.bank_name || '—'}
                                            {c.status && <> · <StatusPill status={c.status} /></>}
                                        </span>
                                    </div>
                                    <span className="font-mono font-semibold text-[#1A1A1A] whitespace-nowrap">
                                        {formatEUR(c.amount)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : chequeLines.length > 0 && (
                        chequeLines.map((l, i) => (
                            <Row
                                key={`chq-${i}`}
                                icon="🧾"
                                label="Cheque"
                                amount={l.amount}
                                testid="pb-line-cheque"
                            />
                        ))
                    )}

                    {/* Due section — visually separated */}
                    {dueLines.length > 0 && (
                        <>
                            <div className="my-2 border-t border-dashed border-[#EFE5E1]" />
                            {dueLines.map((l, i) => (
                                <Row
                                    key={`due-${i}`}
                                    icon="⏳"
                                    label="Due"
                                    amount={l.amount}
                                    extra={sale.is_due_paid ? '(settled)' : undefined}
                                    testid="pb-line-due"
                                />
                            ))}
                        </>
                    )}
                </div>

                {/* Totals footer */}
                <div className="border-t border-[#EFE5E1] bg-[#FAFAFA] px-4 py-2.5 rounded-b-md space-y-1">
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Total Paid</span>
                        <span className="font-mono font-semibold text-[#1A1A1A]" data-testid="pb-total-paid">
                            {formatEUR(breakdown.totalPaid)}
                        </span>
                    </div>
                    {breakdown.outstanding > 0 && (
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-[#E76F51]">Outstanding</span>
                            <span className="font-mono font-semibold text-[#E76F51]" data-testid="pb-outstanding">
                                {formatEUR(breakdown.outstanding)}
                            </span>
                        </div>
                    )}
                    <div className="flex items-center justify-between text-sm pt-0.5 border-t border-[#EFE5E1] mt-1">
                        <span className="text-[#1A1A1A] font-medium">Invoice Total</span>
                        <span className="font-mono font-bold text-[#1A1A1A]" data-testid="pb-invoice-total">
                            {formatEUR(breakdown.invoiceTotal)}
                        </span>
                    </div>
                </div>
            </HoverCardContent>
        </HoverCard>
    );
};

export default PaymentBreakdownPopover;
